import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import i18n from 'i18next';

import { Typo } from 'uikit/content';

import useVersion from '../hooks/useVersion';

import SimpleTabsContainer from '../containers/simpleTabsContainer';

import VersioningCompose from './versioningCompose';
import VersioningModalButton from './versioningModalButton';
import VersioningProperties from './versioningProperties';

import styles from '../styles/versioningModal.module.scss';

const VersioningModal = ({
  fetchVersions = () => {},
  getComposeSchema = () => {},
  getPropertiesAxiosConfig = () => {},
  getPropertiesSchema = () => {},
  getVersionAxiosConfig = () => {},
  onVersionContent = () => {},
  queryParamsToPersist = [],
  version = {},
}) => {
  const { composeSchema, isLoading, isVersioning, model, propertiesSchema, versionContent } =
    useVersion({
      fetchVersions,
      getComposeSchema,
      getPropertiesAxiosConfig,
      getPropertiesSchema,
      getVersionAxiosConfig,
      onVersionContent,
      version,
    });

  const {
    is_current_published: isPublished = version.is_current_published,
    name = version.name,
    version_number: versionNumber = version.version_number,
    version_state_updated_at: updatedAt = version.version_state_updated_at,
  } = model;

  const labelKey = isPublished ? 'version-number-published' : 'version-number-archived';
  const { lang } = document.documentElement || 'en';

  const dateTime = new Date(updatedAt).toLocaleString(lang, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <>
      <Typo className={styles.breadcrumbs} variant="s01">
        {i18n.t('version-name-number', { name, number: versionNumber })}
      </Typo>
      <div className={styles.header}>
        <div className={styles.textContainer}>
          <div className={styles.version}>
            <Typo
              className={classnames(styles.hasDot, isPublished && styles.isPublished)}
              variant="s02"
              weight="medium"
            >
              {i18n.t(labelKey, { number: versionNumber })}
            </Typo>
            <Typo color="secondary" variant="s02">
              {dateTime}
            </Typo>
          </div>
          <Typo element="h3" variant="d05">
            {name}
          </Typo>
        </div>
        <VersioningModalButton
          isPublished={isPublished}
          isVersioning={isVersioning}
          versionContent={versionContent}
        />
      </div>
      <SimpleTabsContainer
        queryParamsToPersist={queryParamsToPersist}
        tabs={[
          {
            id: 'compose',
            title: i18n.t('compose'),
            body: <VersioningCompose isLoading={isLoading} model={model} schema={composeSchema} />,
          },
          {
            id: 'properties',
            title: i18n.t('properties'),
            body: (
              <VersioningProperties isLoading={isLoading} model={model} schema={propertiesSchema} />
            ),
          },
        ]}
      />
    </>
  );
};

VersioningModal.propTypes = {
  fetchVersions: PropTypes.func,
  getComposeSchema: PropTypes.func,
  getPropertiesAxiosConfig: PropTypes.func,
  getPropertiesSchema: PropTypes.func,
  getVersionAxiosConfig: PropTypes.func,
  onVersionContent: PropTypes.func,
  queryParamsToPersist: PropTypes.arrayOf(PropTypes.string),
  version: PropTypes.shape({
    id: PropTypes.number,
    is_current_published: PropTypes.bool,
    version_number: PropTypes.number,
    version_state_updated_at: PropTypes.string,
    version_state_updated_by: PropTypes.string,
  }),
};

export default React.memo(VersioningModal);
